import http from "./http";

const CMS_API_URL = `${import.meta.env.VITE_CMS_API_URL}`;

const articlesEndpoint = CMS_API_URL + "/articles";
const ageGroupsEndpoint = CMS_API_URL + "/age-groups";
const categoriesEndpoint = CMS_API_URL + "/categories";
const policiesEndpoint = CMS_API_URL + "/privacy-policies";
const faqsEndpoint = CMS_API_URL + "/faqs";
const sosCentersEndpoint = CMS_API_URL + "/sos-centers";
const cookiePolicyEndpoint = CMS_API_URL + "/policy-cookies";
const termsOfUseEndpoint = CMS_API_URL + "/terms-of-uses";
const abousUsEndpoint = CMS_API_URL + "/about-us-pages";
const videosEndpoint = CMS_API_URL + "/videos";
const podcastsEndpoint = CMS_API_URL + "/podcasts";
const assessmentResultEndpoint = CMS_API_URL + "/assessment-results";
const moodTrackerEndpoint = CMS_API_URL + "/mood-tracker-recomendations";
const hosnAssetsEndpoint = CMS_API_URL + "/hosn-assets";
const hosnPillarsEndpoint = CMS_API_URL + "/hosn-pillars";

// Program scoped pages (one entry per program and locale, see cms/src/api/utils/program-scoped.js)
const programPageEndpoints = {
  "privacy-policy": policiesEndpoint,
  "terms-of-use": termsOfUseEndpoint,
  "cookie-policy": cookiePolicyEndpoint,
};

/**
 * generate a querry string from an object
 *
 * @param {object} queryObj - the object to generate the querry string from
 * @param {string} queryObj.limit  - the number of documents to return
 * @param {number} queryObj.populate - whether to populate the data fully or not
 * @param {number} queryObj.startFrom - the starting index of the document to return
 * @param {string} queryObj.contains - substring to match in title, labels, category name, body, or body_ck
 * @param {string} queryObj.ageGroupId - the age group id to filter by
 * @param {string} queryObj.categoryId - the category id to filter by
 * @param {number} queryObj.decisionTreeSection - the decision tree section number to filter by
 * @param {string} queryObj.locale - the locale to filter by
 * @param {string} queryObj.sortBy - the field to sort by
 * @param {string} queryObj.sortOrder - the order to sort by, possible values are "asc" and "desc"
 * @param {string} queryObj.excludeId - the id to exclude from the results
 * @param {string} queryObj.countryAlpha2 - the country alpha2 code to filter by
 * @param {string} queryObj.is_playandheal - the play and heal flag to filter by
 *
 */
function generateQuerryString(queryObj) {
  let querry = `?`;

  if (queryObj.locale) querry += `&locale=${queryObj.locale}`;

  if (queryObj.populate) {
    querry += "&populate=*";
  }

  if (queryObj.is_playandheal) {
    querry += `&filters[is_playandheal][$eq]=${queryObj.is_playandheal}`;
  }

  if (queryObj.limit) {
    querry += `&pagination[limit]=${queryObj.limit}`;
  }

  if (queryObj.startFrom) {
    querry += `&pagination[start]=${queryObj.startFrom}`;
  }

  if (queryObj.contains && queryObj.contains !== "") {
    // Title, labels, category, richtext body, or CKEditor body_ck may contain the search string.
    querry += `&filters[$or][0][title][$containsi]=${queryObj.contains}&filters[$or][1][labels][name][$containsi]=${queryObj.contains}&filters[$or][2][category][name][$containsi]=${queryObj.contains}&filters[$or][3][body][$containsi]=${queryObj.contains}&filters[$or][4][body_ck][$containsi]=${queryObj.contains}`;
  }

  if (queryObj.ageGroupId) {
    querry += `&filters[age_groups][id][$in]=${queryObj.ageGroupId}`;
  }

  if (queryObj.categoryId) {
    querry += `&filters[category][id][$in]=${queryObj.categoryId}`;
  }

  if (
    queryObj.decisionTreeSection !== undefined &&
    queryObj.decisionTreeSection !== null
  ) {
    querry += `&filters[decision_tree_section][$eq]=${queryObj.decisionTreeSection}`;
  }

  if (queryObj.sortBy && queryObj.sortOrder) {
    querry += `&sort[0]=${queryObj.sortBy}%3A${queryObj.sortOrder}`;
  }

  if (queryObj.excludeId) {
    querry += `&filters[id][$notIn]=${queryObj.excludeId}`;
  }

  //This was added for the policies
  if (queryObj.countryAlpha2) {
    querry += `&country=${queryObj.countryAlpha2}`;
  }

  if (queryObj.global) {
    querry += `&filters[global][$in]=${queryObj.global}`;
  }

  if (queryObj.listOfIds) {
    for (let i = 0; i < queryObj.listOfIds.length; i++) {
      querry += `&filters[id][$in]=${queryObj.listOfIds[i]}`;
    }
  }

  if (queryObj.ids && queryObj.ids.length > 0) {
    querry += `&ids=${queryObj.ids}`;
  }

  // This was added for the admin to prevent the server to filter the data by selected ids.
  if (queryObj.isForAdmin) {
    querry += `&isForAdmin=${queryObj.isForAdmin}`;
  }

  if (queryObj.platform) {
    querry += `&platform=${queryObj.platform}`;
  }

  if (queryObj.isForGlobal) {
    querry += `&filters[global][$eq]=${queryObj.isForGlobal}&isForAdmin=true`;
  }

  if (querry.includes("?&")) {
    querry = querry.replace("?&", "?");
  }

  if (queryObj.psychological) {
    querry += `&filters[psychological][$eq]=${queryObj.psychological}`;
  }
  if (queryObj.social) {
    querry += `&filters[social][$eq]=${queryObj.social}`;
  }
  if (queryObj.biological) {
    querry += `&filters[biological][$eq]=${queryObj.biological}`;
  }

  return querry;
}

/**
 * send request to get multiple articles
 *
 * @param {object} queryObj - the object to generate the querry string from
 * @param {string} queryObj.limit  - the number of documents to return
 * @param {number} queryObj.populate - whether to populate the data fully or not
 * @param {number} queryObj.startFrom - the starting index of the document to return
 * @param {string} queryObj.contains - substring to match in title, labels, category name, body, or body_ck
 * @param {string} queryObj.ageGroupId - the age group id to filter by
 * @param {string} queryObj.categoryId - the category id to filter by
 * @param {number} queryObj.decisionTreeSection - the decision tree section number to filter by
 * @param {string} queryObj.locale - the locale to filter by
 * @param {string} queryObj.sortBy - the field to sort by
 * @param {string} queryObj.sortOrder - the order to sort by, possible values are "asc" and "desc"
 * @param {string} queryObj.excludeId - the id to exclude from the results
 * @param {string} queryObj.countryAlpha2 - the country alpha2 code to filter by
 * @param {string} queryObj.global - the global flag to filter by
 * @param {string} queryObj.listOfIds - the list of ids to filter by
 * @param {string} queryObj.ids - the ids to filter by
 *
 */

async function getArticles(queryObj) {
  const querryString = generateQuerryString(queryObj);

  const { data } = await http.get(`${articlesEndpoint}${querryString}`);

  return { data };
}

/**
 * send request to get data for a specific article by id
 *
 * @param {string} id - the id of the article
 * @param {string} locale - the locale for which to retrieve articles
 *
 * @returns {object} the article data
 */
async function getArticleById(id, locale) {
  const querryString = generateQuerryString({ populate: true, locale: locale });

  try {
    // Increment read count for the given article id
    await addArticleReadCount(id);
  } catch (error) {
    console.log(error);
  }

  const { data } = await http.get(`${articlesEndpoint}/${id}${querryString}`);

  return data;
}

/**
 * send request to get available locales for a specific article, by id - Only used for testing from Postman
 *
 * @param {string} id - the id of the article
 *
 * @returns {object} all available article locales e.g. {"kk": 17,"en": 12,"ru": 18}
 */
async function getArticleLocales(id) {
  const { data } = await http.get(
    `${articlesEndpoint}/getArticleLocales/${id}`
  );

  return data;
}

/**
 * send request to get all unique category IDs from articles
 *
 * @param {string} locale - the locale for which to retrieve category IDs
 * @param {number} ageGroupId - the age group ID to filter by
 * @param {array} articleIds - array of article IDs to filter by (optional)
 *
 * @returns {array} array of unique category IDs
 */
async function getArticleCategoryIds(locale, ageGroupId, articleIds) {
  let queryString = "?locale=" + locale;

  if (ageGroupId) {
    queryString += "&ageGroupId=" + ageGroupId;
  }

  if (articleIds && articleIds.length > 0) {
    queryString += "&ids=" + articleIds.join(",");
  }

  const { data } = await http.get(
    articlesEndpoint + "/custom/category-ids" + queryString
  );
  return data;
}

/**
 * send request to get all unique category IDs from podcasts
 *
 * @param {string} locale - the locale for which to retrieve category IDs
 * @param {array} podcastIds - array of podcast IDs to filter by (optional)
 *
 * @returns {array} array of unique category IDs
 */
async function getPodcastCategoryIds(locale, podcastIds) {
  let queryString = "?locale=" + locale;

  if (podcastIds && podcastIds.length > 0) {
    queryString += "&ids=" + podcastIds.join(",");
  }

  const { data } = await http.get(
    podcastsEndpoint + "/custom/category-ids" + queryString
  );
  return data;
}

/**
 * send request to get all unique category IDs from videos
 *
 * @param {string} locale - the locale for which to retrieve category IDs
 * @param {array} videoIds - array of video IDs to filter by (optional)
 *
 * @returns {array} array of unique category IDs
 */
async function getVideoCategoryIds(locale, videoIds) {
  let queryString = "?locale=" + locale;

  if (videoIds && videoIds.length > 0) {
    queryString += "&ids=" + videoIds.join(",");
  }

  const { data } = await http.get(
    videosEndpoint + "/custom/category-ids" + queryString
  );
  return data;
}

/**
 * send request to get all the categories
 *
 * @param {string} locale - the locale for which to retrieve categories
 *
 * @returns {object} the categories data
 */

async function getCategories(locale) {
  const querryString = generateQuerryString({
    locale: locale,
  });

  const { data } = await http.get(`${categoriesEndpoint}${querryString}`);
  return data;
}

//--------------------- PUT Requests ---------------------//;
/**
 * send request toincrement article count
 *
 * @param {string} id - the id of the article
 *
 * @returns {object} the ageGroups data
 */
async function addArticleReadCount(id) {
  return http.put(`${articlesEndpoint}/addReadCount/${id}`);
}

//--------------------- PUT Requests ---------------------//;
/**
 * send request to increment download count for an article
 *
 * @param {string} id - the id of the article
 *
 * @returns {object} the ageGroups data
 */
async function addArticleDownloadCount(id) {
  return http.put(`${articlesEndpoint}/addDownloadCount/${id}`);
}

//--------------------- PUT Requests ---------------------//;
/**
 * send request to increment share count for an article
 *
 * @param {string} id - the id of the article
 *
 * @returns {object} the ageGroups data
 */
async function addArticleShareCount(id) {
  return http.put(`${articlesEndpoint}/addShareCount/${id}`);
}

//--------------------- Categories ---------------------//;
/**
 * send request to get all ageGroups
 *
 * @param {string} locale - the locale for which to retrieve ageGroups
 *
 * @returns {object} the ageGroups data
 */
async function getAgeGroups(locale) {
  const querryString = generateQuerryString({
    locale: locale,
  });
  const { data } = await http.get(`${ageGroupsEndpoint}${querryString}`);
  return data;
}

//--------------------- Policies ---------------------//;
/**
 * send request to get privacy policies
 * @param {string} locale - the locale for which to retrieve policies
 * @returns {string} countryAlpha2 - the country 2 characters ISO-3166 code for which to retrieve policies
 * @returns {string} platform - the platform for which to retrieve policies e.g website, client or provider
 * @returns {object} the policies data
 *
 */
async function getPolicies(locale, country, platform, is_playandheal) {
  let queryParams = {
    locale,
    platform,
  };

  if (country === "GLOBAL") {
    queryParams.global = true;
  } else if (is_playandheal) {
    queryParams.is_playandheal = true;
  } else {
    queryParams.countryAlpha2 = country;
  }

  const queryString = generateQuerryString(queryParams);

  let res = await http.get(`${policiesEndpoint}/find${queryString}`);

  return res;
}

//--------------------- Cookie Policy ---------------------//;
/**
 * send request to Cookie Policy
 * @param {string} locale - the locale for which to retrieve Cookie Policy
 * @returns {string} countryAlpha2 - the country 2 characters ISO-3166 code for which to retrieve Cookie Policy
 * @returns {string} platform - the platform for which to retrieve Cookie Policy e.g website, client or provider
 * @returns {object} the Cookie Policy data
 *
 */
async function getCookiePolicy(
  locale,
  countryAlpha2,
  platform,
  is_playandheal
) {
  let queryParams = {
    locale: locale,
    platform: platform,
    countryAlpha2: countryAlpha2,
  };

  if (is_playandheal) {
    queryParams.is_playandheal = true;
  }

  const querryString = generateQuerryString(queryParams);
  let res = await http.get(`${cookiePolicyEndpoint}/find${querryString}`);

  return res;
}

//--------------------- Terms Of Use ---------------------//;
/**
 * send request to terms of use
 * @param {string} locale - the locale for which to retrieve Terms Of Use
 * @returns {string} countryAlpha2 - the country 2 characters ISO-3166 code for which to retrieve Terms Of Use
 * @returns {string} platform - the platform for which to retrieve Terms Of Use e.g website, client or provider
 * @returns {object} the Terms Of Use data
 *
 */
async function getTermsOfUse(locale, country, platform, is_playandheal) {
  let queryParams = {
    locale,
    platform,
  };

  if (country === "GLOBAL") {
    queryParams.global = true;
  } else if (is_playandheal) {
    queryParams.is_playandheal = true;
  } else {
    queryParams.countryAlpha2 = country;
  }

  const queryString = generateQuerryString(queryParams);

  let res = await http.get(`${termsOfUseEndpoint}/find${queryString}`);

  return res;
}

//--------------------- FAQs ---------------------//;
/**
 * send request to get FAQs
 *
 * @returns {object} the FAQs data
 *
 */
async function getFAQs(queryObj) {
  const querryString = generateQuerryString(queryObj);

  const { data } = await http.get(`${faqsEndpoint}${querryString}`);

  return { data };
}

async function getGlobalFAQs(queryObj) {
  const querryString = generateQuerryString({
    ...queryObj,
    isForGlobal: true,
  });

  const { data } = await http.get(`${faqsEndpoint}${querryString}`);

  return { data };
}

/**
 * send request to get available locales for a specific FAQ, by id
 *
 * @param {string} id - the id of the FAQ
 *
 * @returns {object} all available FAQ locales e.g. {"kk": 17,"en": 12,"ru": 18}
 */
async function getFAQAvailableLocales(id) {
  const { data } = await http.get(`${faqsEndpoint}/available-locales/${id}`);

  return data;
}

//--------------------- SOS Centers ---------------------//;
/**
 * send request to get SOS Centers
 *
 * @returns {object} the sos centers data
 *
 */
async function getSOSCenters(queryObj) {
  const querryString = generateQuerryString(queryObj);

  const { data } = await http.get(`${sosCentersEndpoint}${querryString}`);

  return { data };
}

/**
 * send request to get available locales for a specific SOS Center, by id
 *
 * @param {string} id - the id of the SOS Center
 *
 * @returns {object} all available SOS Center locales e.g. {"kk": 17,"en": 12,"ru": 18}
 */
async function getSOSCenterAvailableLocales(id) {
  const { data } = await http.get(
    `${sosCentersEndpoint}/available-locales/${id}`
  );

  return data;
}

async function getAbousUsContentForCountry({
  country,
  language,
  is_playandheal,
}) {
  let queryParams = {
    locale: language,
  };

  if (country === "GLOBAL") {
    queryParams.global = true;
  } else if (is_playandheal) {
    queryParams.is_playandheal = true;
  } else {
    queryParams.countryAlpha2 = country;
  }

  const querryString = generateQuerryString(queryParams);
  let res = await http.get(`${abousUsEndpoint}/find${querryString}`);

  return res.data;
}

async function addRating({ id, action, contentType }) {
  const endpoint =
    contentType === "article"
      ? articlesEndpoint
      : contentType === "video"
      ? videosEndpoint
      : contentType === "podcast"
      ? podcastsEndpoint
      : null;
  return http.put(`${endpoint}/addRating/${id}`, { action });
}

async function getVideos(queryObj) {
  const querryString = generateQuerryString(queryObj);
  const { data } = await http.get(`${videosEndpoint}${querryString}`);

  return { data };
}

async function getVideoById(id, locale = "en") {
  const querryString = generateQuerryString({ populate: true, locale });

  const { data } = await http.get(`${videosEndpoint}/${id}${querryString}`);

  return data;
}

async function getVideoLocales(id) {
  const { data } = await http.get(`${videosEndpoint}/available-locales/${id}`);

  return data;
}

async function getPodcasts(queryObj) {
  const querryString = generateQuerryString(queryObj);
  const { data } = await http.get(`${podcastsEndpoint}${querryString}`);

  return { data };
}

async function getPodcastById(id, locale = "en") {
  const querryString = generateQuerryString({ populate: true, locale });

  const { data } = await http.get(`${podcastsEndpoint}/${id}${querryString}`);

  return data;
}

async function getPodcastLocales(id) {
  const { data } = await http.get(
    `${podcastsEndpoint}/available-locales/${id}`
  );

  return data;
}

async function addPodcastShareCount(id) {
  return http.put(`${podcastsEndpoint}/addShareCount/${id}`);
}

async function addVideoShareCount(id) {
  return http.put(`${videosEndpoint}/addShareCount/${id}`);
}

/**
 * Get comprehensive statistics for all categories
 * @param {string} contentType - the type of content to get statistics for e.g. articles, videos, podcasts, all
 * @returns {object} comprehensive category statistics
 */
async function getAllCategoriesStatistics({
  contentType,
  sex,
  yearOfBirthFrom,
  yearOfBirthTo,
  urbanRural,
  startDate,
  endDate,
}) {
  let queryString = `?contentType=${contentType}`;
  if (sex) queryString += `&sex=${sex}`;
  if (yearOfBirthFrom) queryString += `&yearOfBirthFrom=${yearOfBirthFrom}`;
  if (yearOfBirthTo) queryString += `&yearOfBirthTo=${yearOfBirthTo}`;
  if (urbanRural) queryString += `&urbanRural=${urbanRural}`;
  if (startDate) queryString += `&startDate=${startDate}`;
  if (endDate) queryString += `&endDate=${endDate}`;
  const { data } = await http.get(
    `${CMS_API_URL}/category-statistics/all${queryString}`
  );
  return data;
}

async function getRecommendedArticlesForCategory(payload) {
  const { data } = await http.put(
    `${articlesEndpoint}/recommended/category`,
    payload
  );

  return data;
}

/**
 * Get assessment result based on psychological, social, and biological scores
 * @param {object} queryObj - the object containing query parameters
 * @param {string} queryObj.language - the language/locale for the results
 * @param {number} queryObj.psychological - 'low', 'moderate', 'high'
 * @param {number} queryObj.social - 'low', 'moderate', 'high'
 * @param {number} queryObj.biological - 'low', 'moderate', 'high'
 * @returns {object} assessment result data
 */
async function getAssessmentResult(queryObj) {
  const queryString = `?populate[articles][populate]=*&populate[podcasts][populate]=*&populate[videos][populate]=*&filters[psychological][$eq]=${queryObj.psychological}&filters[social][$eq]=${queryObj.social}&filters[biological][$eq]=${queryObj.biological}&locale=${queryObj.language}`;

  const { data } = await http.get(`${assessmentResultEndpoint}${queryString}`);

  return { data };
}

async function getMoodTrackerRecommendations(moodType, locale) {
  const queryString = `?populate[articles][populate]=*&populate[podcasts][populate]=*&populate[videos][populate]=*&filters[mood][$eq]=${moodType}&locale=${locale}`;

  const { data } = await http.get(`${moodTrackerEndpoint}${queryString}`);

  return { data };
}

//--------------------- Programs ---------------------//;
/**
 * send request to get a legal page of a program (e.g. Hosn El Hal)
 * @param {string} page - "privacy-policy" | "terms-of-use" | "cookie-policy"
 * @param {string} locale - the locale for which to retrieve the page
 * @param {string} program - the program e.g "hosnelhal"
 * @param {string} platform - "website" | "client" | "provider"
 * @returns {object} the page content
 */
async function getProgramPolicy(page, locale, program, platform = "website") {
  return http.get(`${programPageEndpoints[page]}/find`, {
    params: { locale, program, platform },
  });
}

//--------------------- Hosn El Hal ---------------------//;
const HOSN_ASSET_POPULATE = {
  "populate[cover_image]": "*",
  "populate[video]": "*",
  "populate[audio]": "*",
  "populate[file_web]": "*",
  "populate[file_print]": "*",
  "populate[file_mobile]": "*",
  "populate[tags]": "*",
  "populate[pillar]": "*",
};

/**
 * send request to get Hosn El Hal assets
 * @param {object} queryObj
 * @param {string} queryObj.locale - the locale, "all" for every language
 * @param {string} queryObj.search - substring to match in the title or description
 * @param {string} queryObj.format - "video" | "audio" | "visual" | "worksheet"
 * @param {string} queryObj.pillar - key of a hosn-pillar
 * @param {number} queryObj.minDuration - minimum duration in minutes (inclusive)
 * @param {number} queryObj.maxDuration - maximum duration in minutes (exclusive)
 * @param {number[]} queryObj.ids - only these assets (returned in id order)
 * @param {number} queryObj.limit - the number of assets to return
 * @returns {object} the assets data
 */
async function getHosnAssets({
  locale,
  search,
  format,
  pillar,
  minDuration,
  maxDuration,
  ids,
  limit = 100,
}) {
  const params = {
    locale,
    ...HOSN_ASSET_POPULATE,
    // In the order the assets were added
    "sort[0]": "id:asc",
    "pagination[limit]": limit,
  };

  // Search and length are each an $or, so they are combined with $and
  let group = 0;
  if (search) {
    params[`filters[$and][${group}][$or][0][title][$containsi]`] = search;
    params[`filters[$and][${group}][$or][1][description][$containsi]`] = search;
    group += 1;
  }
  if (format) params["filters[format][$eq]"] = format;
  if (pillar) params["filters[pillar][key][$eq]"] = pillar;
  ids?.forEach((id, index) => {
    params[`filters[id][$in][${index}]`] = id;
  });

  // The length filter is in minutes, the length is stored in seconds on the
  // video or audio. Cards show the rounded minutes, so the boundaries move
  // half a minute down - a 4:52 asset shows "5 min" and is not "Under 5 min".
  if (minDuration !== undefined || maxDuration !== undefined) {
    const toSeconds = (minutes) => (minutes - 0.5) * 60;
    ["video", "audio"].forEach((media, index) => {
      const key = `filters[$and][${group}][$or][${index}][${media}][duration_seconds]`;
      if (minDuration !== undefined) params[`${key}[$gte]`] = toSeconds(minDuration);
      if (maxDuration !== undefined) params[`${key}[$lt]`] = toSeconds(maxDuration);
    });
  }

  return http.get(hosnAssetsEndpoint, { params });
}

/**
 * send request to get a Hosn El Hal asset
 * @param {number} id - the id of the asset
 * @returns {object} the asset data
 */
async function getHosnAssetById(id) {
  return http.get(`${hosnAssetsEndpoint}/${id}`, {
    params: HOSN_ASSET_POPULATE,
  });
}

/**
 * send request to get the ids of all localizations of an asset
 * @param {number} id - the id of the asset
 * @returns {object} e.g {"en": 12, "ar": 17}
 */
async function getHosnAssetLocales(id) {
  return http.get(`${hosnAssetsEndpoint}/available-locales/${id}`);
}

/**
 * send request to get the Hosn El Hal pillars (the topics of the assets)
 * @param {string} locale - the locale for which to retrieve the pillars
 * @returns {object} the pillars data, in pillar order
 */
async function getHosnPillars(locale) {
  return http.get(hosnPillarsEndpoint, {
    params: { locale, "sort[0]": "order:asc", "pagination[limit]": 100 },
  });
}

async function addHosnAssetViewCount(id) {
  return http.put(`${hosnAssetsEndpoint}/addViewCount/${id}`);
}

async function addHosnAssetDownloadCount(id) {
  return http.put(`${hosnAssetsEndpoint}/addDownloadCount/${id}`);
}

export default {
  getProgramPolicy,
  getHosnAssets,
  getHosnAssetById,
  getHosnAssetLocales,
  getHosnPillars,
  addHosnAssetViewCount,
  addHosnAssetDownloadCount,
  getArticles,
  getArticleById,
  getArticleLocales,
  getArticleCategoryIds,
  getPodcastCategoryIds,
  getVideoCategoryIds,
  getCategories,
  getAgeGroups,
  addArticleReadCount,
  getPolicies,
  getCookiePolicy,
  getTermsOfUse,
  getFAQs,
  getFAQAvailableLocales,
  getSOSCenters,
  getSOSCenterAvailableLocales,
  getAbousUsContentForCountry,
  getGlobalFAQs,
  addRating,
  getVideos,
  getVideoById,
  getVideoLocales,
  getPodcasts,
  getPodcastById,
  getPodcastLocales,
  addArticleDownloadCount,
  addArticleShareCount,
  addPodcastShareCount,
  addVideoShareCount,
  getAllCategoriesStatistics,
  getRecommendedArticlesForCategory,
  getAssessmentResult,
  getMoodTrackerRecommendations,
};
